import { useState } from "react";

import union from "lodash/union";
import without from "lodash/without";
import { useHotkeys } from "react-hotkeys-hook";
import { useMediaQuery } from "react-responsive";

import LazyImg from "@/components/helpers/ui/LazyImg";
import { Box, Flex } from "@/components/layout/flexbox";
import { isXsQuery, Media } from "@/components/layout/Media";
import { MatchingCategoryComponentProps } from "@/components/pages_flexible/match/utils";
import FadeInBox from "@/components/pieces/FadeInBox";
import Checkbox from "@/components/pieces/form/Checkbox";
import CtaButton from "@/components/pieces/onboarding/CtaButton";
import OptionCard from "@/components/pieces/onboarding/OptionCard";
import PageTitle from "@/components/pieces/onboarding/PageTitle";
import { BRAND_PURPLE, SUBTEXT_COLOR } from "@/css/constants";

const MAX_WIDTH = 800;

interface OptionProps {
  label: string;
  description?: string;
  iconUrl?: string;
  selected: boolean;
  onClick: () => void;
  style?: any;
}

function Option({
  label,
  description,
  iconUrl,
  selected,
  onClick,
  style,
}: OptionProps) {
  return (
    <OptionCard
      interactive={true}
      onClick={onClick}
      elevation={2}
      p={0}
      className={selected ? "active" : null}
      style={style}
    >
      <Flex justifyContent="space-between" alignItems="center" p={3}>
        <Flex
          pr={24}
          alignItems="center"
          style={{
            transition: "color 200ms ease",
          }}
        >
          <Media greaterThan="xs">
            {iconUrl ? (
              <Box mr={3}>
                <LazyImg width={60} src={iconUrl} />
              </Box>
            ) : null}
          </Media>
          <div>
            <div>
              <b>{label}</b>
            </div>
            {description ? (
              <Box color={SUBTEXT_COLOR} mt={1}>
                {description}
              </Box>
            ) : null}
          </div>
        </Flex>
        <Checkbox
          hasBorders={false}
          onChange={() => null}
          checked={selected}
          checkedColor={BRAND_PURPLE}
        />
      </Flex>
    </OptionCard>
  );
}

function MultipleChoice({
  loading = false,
  description,
  initialSelectedIds,
  options,
  onSubmit,
}: MatchingCategoryComponentProps) {
  const [selectedOptionIds, setSelectedOptionIds] =
    useState(initialSelectedIds);

  const _onSubmit = () => {
    onSubmit(selectedOptionIds);
  };

  useHotkeys("enter", _onSubmit, {}, [selectedOptionIds]);

  const isXs = useMediaQuery(isXsQuery);
  const fontSize = isXs ? 16 : 18;

  return (
    <Flex justifyContent="center">
      <Box
        width={[1, 1, MAX_WIDTH]}
        style={{ fontSize, paddingBottom: isXs ? 80 : null }}
      >
        <FadeInBox>
          <PageTitle
            title={description}
            subtitle="Check all that apply or leave blank"
          ></PageTitle>
          <Box mx={-2}>
            {options.map((option) => {
              const selected = selectedOptionIds.includes(option.id);
              return (
                <Box key={option.id} px={2} my={3}>
                  <Option
                    label={option.name}
                    description={option.description}
                    iconUrl={option.iconUrl}
                    selected={selected}
                    onClick={() => {
                      setSelectedOptionIds(
                        selected
                          ? without(selectedOptionIds, option.id)
                          : union(selectedOptionIds, [option.id]),
                      );
                    }}
                  />
                </Box>
              );
            })}
          </Box>
          <CtaButton
            visible
            loading={loading}
            onClick={_onSubmit}
            floating={true}
            maxWidth={MAX_WIDTH}
          >
            Onwards
          </CtaButton>
        </FadeInBox>
      </Box>
    </Flex>
  );
}

export default MultipleChoice;
